import type { ShouldRevalidateFunction } from 'react-router';
import type BoltMetaArgs from 'types/BoltMetaArgs';
import type { HomePage } from 'types/capi/homePage';

import useEnv from '~/hooks/useEnv';
import usePageData from '~/hooks/usePageData';
import useRegion from '~/hooks/useRegion';
import useStructuredData from '~/hooks/useStructuredData';
import CopOrDrop from '~/modules/CopOrDrop';
import RecentFeatures from '~/modules/RecentFeatures';
import Splash from '~/modules/Splash';
import { getAlternateTagsForAllRegions } from '~/utils/alternateTags';
import seo from '~/utils/seo';

import AppBanner from '../../modules/AppBanner';
import ArticleCarousel from '../../modules/ArticleCarousel';
import BannerCarousel from '../../modules/BannerCarousel';
import HeaderCarousel from '../../modules/HeaderCarousel';
import HotDeals from '../../modules/HotDeals';
import MarketplaceTopSellers from '../../modules/MarketplaceTopSellers';
import PopularBrands from '../../modules/PopularBrands';
import PopularProducts from '../../modules/PopularProducts';
import ProductList from '../../modules/ProductList';
import SocialMediaCarousel from '../../modules/SocialMediaCarousel';
import SubBrands from '../../modules/SubBrands';
import TopCategories from '../../modules/TopCategories';
import UpcomingProducts from '../../modules/UpcomingProducts';
import Usps from '../../modules/Usps';
import LaunchPage from './LaunchPage';

export const handle = { i18n: ['home'] };

export default function Home() {
  const env = useEnv();
  const { modules, _schemas } = usePageData<HomePage>();

  useStructuredData(_schemas);

  const region = useRegion();

  if (region.code !== 'GB' && !env.FF_INTERNATIONAL_HOMEPAGE)
    return <LaunchPage />;

  return (
    <>
      <h1 className="hidden">The Sole Supplier</h1>
      {modules.map((module, i) => {
        switch (module.type) {
          case 'SPLASH':
            return <Splash key={`${module.type}-${i}`} module={module} />;
          case 'HEADER_CAROUSEL':
            return (
              <HeaderCarousel
                variant="base"
                analytics={{ id: 'HOMEPAGE_CAROUSEL', position: 'Homepage' }}
                key={`${module.type}-${i}`}
                module={module}
              />
            );
          case 'SUB_BRANDS':
            return (
              <SubBrands key={`${module.type}-${i}`} subBrands={module.items} />
            );
          case 'POPULAR_BRANDS':
            return (
              <PopularBrands
                key={`${module.type}-${i}`}
                brands={module.items}
              />
            );
          case 'LATEST_NEWS':
            return (
              <ArticleCarousel
                key={`${module.type}-${i}`}
                items={module.items}
                title="Latest News"
              />
            );
          case 'MARKETPLACE_TOP_SELLERS':
            return (
              <MarketplaceTopSellers
                key={`${module.type}-${i}`}
                items={module.items}
                backdrop={module.backdrop}
              />
            );
          case 'APP_BANNER':
            return (
              <AppBanner key={`${module.type}-${i}`} items={module.items} />
            );
          case 'SEASONAL_BANNER':
            return (
              <BannerCarousel key={`${module.type}-${i}`} slides={[module]} />
            );
          case 'POPULAR_PRODUCTS':
            return (
              <PopularProducts
                key={`${module.type}-${i}`}
                items={module.items}
              />
            );
          case 'HOT_DEALS':
            return (
              <HotDeals key={`${module.type}-${i}`} items={module.items} />
            );
          case 'COP_OR_DROP':
            return <CopOrDrop key={`${module.type}-${i}`} />;
          case 'UPCOMING_PRODUCTS':
            return (
              <UpcomingProducts
                key={`${module.type}-${i}`}
                items={module.items}
              />
            );
          case 'TOP_CATEGORIES':
            return (
              <TopCategories key={`${module.type}-${i}`} categories={module} />
            );
          case 'USPS':
            return <Usps key={`${module.type}-${i}`} data={module} />;
          case 'PRODUCT_LIST':
            return <ProductList key={`${module.type}-${i}`} module={module} />;
          case 'RECENT_FEATURES':
            return (
              <RecentFeatures
                title="Latest Features"
                key={`${module.type}-${i}`}
                items={module.items}
              />
            );
          case 'SOCIAL_MEDIA_CAROUSEL':
            return (
              <SocialMediaCarousel
                key={`${module.type}-${i}`}
                module={module}
              />
            );
          default:
            return null;
        }
      })}
    </>
  );
}

export const shouldRevalidate: ShouldRevalidateFunction = ({
  defaultShouldRevalidate,
  formAction
}) => {
  if (formAction?.startsWith('/bolt/cod')) return false;

  return defaultShouldRevalidate;
};

type LoaderData = {
  pageData: HomePage;
};

export const meta = (metaArgs: BoltMetaArgs<LoaderData>) => {
  const { data } = metaArgs;

  const withSeo = data
    ? seo.formatForMeta(data.pageData.canonical_url, data.pageData.seo)
    : [];

  return withSeo.concat(getAlternateTagsForAllRegions(metaArgs));
};

export { loader } from './loader.server';
